import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Card, CardActionArea, CardContent, Chip, CircularProgress,
  Divider, Grid, Paper, Stack, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import api from '../api';
import { useAuth } from '../AuthContext';

const CATEGORIES = {
  gas_fuel: 'Gas / Fuel', feed: 'Feed', hay: 'Hay',
  equipment: 'Equipment', vet: 'Veterinary', misc: 'Miscellaneous',
};
const STATUS_COLORS = { active: 'success', sold: 'warning', deceased: 'error' };
const SOURCE_LABELS = { cow_sale: 'Cow Sale', nrcs: 'NRCS / Farm Bill', other: 'Other' };

function StatCard({ label, value, sub, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', flex: 1, minWidth: 120 }}>
      <Typography variant="h5" fontWeight={700} color={color ?? 'text.primary'}>{value}</Typography>
      <Typography variant="body2" fontWeight={600}>{label}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

export default function MyInfo() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [cows, setCows] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [income, setIncome] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cowTab, setCowTab] = useState('active');

  const fetchAll = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError('');
    try {
      const [cowsRes, receiptsRes, incomeRes] = await Promise.all([
        api.get('/cows/', { params: { owner: user.id } }),
        api.get('/receipts/', { params: { allocated_to: user.id } }),
        api.get('/income/', { params: { farmer: user.id } }),
      ]);
      setCows(cowsRes.data);
      setReceipts(receiptsRes.data);
      setIncome(incomeRes.data);
    } catch {
      setError('Failed to load your information.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const activeCows = cows.filter((c) => c.status === 'active');
  const soldCows = cows.filter((c) => c.status === 'sold');
  const displayedCows = cowTab === 'active' ? activeCows : soldCows;

  const myAllocations = receipts.map((r) => ({
    ...r,
    myAmount: parseFloat(r.allocations?.find((a) => a.farmer === user?.id)?.amount ?? 0),
  }));
  const totalExpenses = myAllocations.reduce((s, r) => s + r.myAmount, 0);
  const totalIncome = income.reduce((s, i) => s + parseFloat(i.amount), 0);
  const netPosition = totalIncome - totalExpenses;

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: 'auto' }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1.5} mb={3}>
        <PersonIcon color="primary" sx={{ fontSize: 36 }} />
        <Box>
          <Typography variant="h4" fontWeight={700}>My Info</Typography>
          <Typography variant="body2" color="text.secondary">{user?.username}</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary strip */}
      <Box display="flex" gap={2} flexWrap="wrap" mb={4}>
        <StatCard label="Active Cows" value={activeCows.length} />
        <StatCard label="Allocated Expenses" value={`$${totalExpenses.toFixed(2)}`} color="error.main" />
        <StatCard label="Income" value={`$${totalIncome.toFixed(2)}`} color="success.main" />
        <StatCard
          label="Net Position"
          value={`${netPosition >= 0 ? '+' : ''}$${Math.abs(netPosition).toFixed(2)}`}
          color={netPosition >= 0 ? 'success.main' : 'error.main'}
          sub={netPosition >= 0 ? 'ahead' : 'behind'}
        />
      </Box>

      {/* ── My Cows ── */}
      <Typography variant="h6" fontWeight={600} mb={1}>My Cows</Typography>
      <Tabs value={cowTab} onChange={(_, v) => setCowTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Active (${activeCows.length})`} value="active" />
        <Tab label={`Sold (${soldCows.length})`} value="sold" />
      </Tabs>

      {displayedCows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', mb: 4 }}>
          <Typography color="text.secondary">No {cowTab} cattle on record.</Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5} mb={4}>
          {displayedCows.map((cow) => (
            <Card key={cow.id} variant="outlined">
              <CardActionArea onClick={() => navigate(`/herd/${cow.id}`)} aria-label={`View cow ${cow.cow_number}`}>
                <CardContent sx={{ py: 1.5 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="h6" fontWeight={700}>{cow.cow_number}</Typography>
                    <Chip size="small" label={cow.status} color={STATUS_COLORS[cow.status] ?? 'default'} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {cow.sex}{cow.breed ? ` · ${cow.breed}` : ''} · DOB: {cow.dob ?? '—'}
                  </Typography>
                  {cow.sold_date && (
                    <Typography variant="body2" color="text.secondary">
                      Sold {cow.sold_date}{cow.sold_amount ? ` · $${parseFloat(cow.sold_amount).toFixed(2)}` : ''}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Tag</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Sex</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Breed</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>DOB</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Age</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Status</TableCell>
                {cowTab === 'sold' && <TableCell scope="col" sx={{ fontWeight: 700 }} align="right">Sale Price</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedCows.map((cow) => (
                <TableRow
                  key={cow.id}
                  hover
                  tabIndex={0}
                  sx={{ cursor: 'pointer', '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 } }}
                  onClick={() => navigate(`/herd/${cow.id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/herd/${cow.id}`); } }}
                  aria-label={`View cow ${cow.cow_number}`}
                >
                  <TableCell sx={{ fontWeight: 600 }}>{cow.cow_number}</TableCell>
                  <TableCell>{cow.sex}</TableCell>
                  <TableCell>{cow.breed || '—'}</TableCell>
                  <TableCell>{cow.dob ?? '—'}</TableCell>
                  <TableCell>{cow.age != null ? `${cow.age} yr` : '—'}</TableCell>
                  <TableCell><Chip size="small" label={cow.status} color={STATUS_COLORS[cow.status] ?? 'default'} /></TableCell>
                  {cowTab === 'sold' && (
                    <TableCell align="right">
                      {cow.sold_amount ? `$${parseFloat(cow.sold_amount).toFixed(2)}` : '—'}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* ── My Income ── */}
      <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={1}>
        <Typography variant="h6" fontWeight={600}>My Income</Typography>
        <Typography variant="subtitle2" color="success.main" fontWeight={700}>
          Total: ${totalIncome.toFixed(2)}
        </Typography>
      </Box>

      {income.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', mb: 4 }}>
          <Typography color="text.secondary">No income records yet.</Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1} mb={4}>
          {income.map((item) => (
            <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" fontWeight={600}>{item.description}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.date} · <Chip size="small" label={SOURCE_LABELS[item.source_type] ?? item.source_type} sx={{ height: 18, fontSize: 11 }} />
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight={700} color="success.main">
                  ${parseFloat(item.amount).toFixed(2)}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Description</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Cow</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {income.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.date}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>
                    <Chip size="small" label={SOURCE_LABELS[item.source_type] ?? item.source_type} />
                  </TableCell>
                  <TableCell>
                    {item.cow_number ? (
                      <Typography
                        variant="body2"
                        sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 600 }}
                        onClick={() => navigate(`/herd/${item.cow}`)}
                      >
                        {item.cow_number}
                      </Typography>
                    ) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
                    ${parseFloat(item.amount).toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* ── My Allocated Expenses ── */}
      <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={1}>
        <Typography variant="h6" fontWeight={600}>My Allocated Expenses</Typography>
        <Typography variant="subtitle2" color="error.main" fontWeight={700}>
          Total: ${totalExpenses.toFixed(2)}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Your share of all joint receipts where you have an allocation.
      </Typography>

      {myAllocations.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">No allocated receipts yet.</Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1}>
          {myAllocations.map((r) => (
            <Paper key={r.id} variant="outlined" sx={{ p: 1.5 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ flex: 1, mr: 1 }}>
                  <Typography variant="body2" fontWeight={600}>{r.description}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {r.date}{r.receipt_number ? ` · #${r.receipt_number}` : ''} · {CATEGORIES[r.category] ?? r.category}
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight={700} color="error.main">
                  ${r.myAmount.toFixed(2)}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Description</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Paid By</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Receipt #</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }} align="right">My Share</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {myAllocations.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.date}</TableCell>
                  <TableCell>{r.description}</TableCell>
                  <TableCell>
                    <Chip size="small" label={CATEGORIES[r.category] ?? r.category} />
                  </TableCell>
                  <TableCell>{r.paid_by_name}</TableCell>
                  <TableCell sx={{ color: r.receipt_number ? 'inherit' : 'text.disabled', fontStyle: r.receipt_number ? 'normal' : 'italic' }}>
                    {r.receipt_number || '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'error.main' }}>
                    ${r.myAmount.toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
